// 🆕 2026-09-02 · Netz-Selfcal-Vorschlag (Stufe 4).
//
// Das LOO-v8-Gate ist bestanden (Median 2,89 px, Anker zurückgehalten) —
// Shots OHNE manuelle Anker bekommen ihre Kamera-Pose aus reinen
// Netz-Beobachtungen. Der Rechenweg ist teuer (Ernte ~3 s je Frame +
// geteilter Bündelausgleich), darum läuft er als bewusst angestoßener
// Job; das Ergebnis wird wie beim Feld-Fit VORGELEGT, nie still
// angewandt. Übernommene Posen greifen nur in Shots ohne Anker-Selfcal
// und hinter einem eigenen Provider-Gate.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  fehlerText, getNetzSelfcalVorschlag, netzSelfcalVorschlagAblehnen,
  netzSelfcalVorschlagStarten, netzSelfcalVorschlagUebernehmen, pollJob,
} from "../../lib/api";

export default function NetzSelfcalVorschlag({ seqId, refreshKey = 0, onApplied }) {
  const [daten, setDaten] = useState(null);
  const [laufend, setLaufend] = useState(false);
  const [meldung, setMeldung] = useState("");

  const laden = useCallback(() => {
    if (!seqId) return;
    getNetzSelfcalVorschlag(seqId)
      .then((d) => setDaten(d || null))
      .catch(() => setDaten(null));
  }, [seqId]);

  useEffect(() => { laden(); }, [laden, refreshKey]);

  // Solange serverseitig ein Lauf aktiv ist (laufender_job liefert die
  // GET-Route), alle 10 s nachladen: so zeigt das Panel den Fortschritt
  // auch nach einem Seiten-Reload und bietet den Start-Knopf nicht
  // erneut an (Doppelstart-Schutz).
  useEffect(() => {
    if (!daten?.laufender_job?.id) return undefined;
    const t = setInterval(laden, 10000);
    return () => clearInterval(t);
  }, [daten?.laufender_job?.id, laden]);

  if (!daten) return null;
  const v = daten.vorschlag || null;
  const offen = v?.status === "vorschlag" && !v?.entschieden;
  // Rechnen lohnt, wenn es etwas zu ernten gibt oder schon ≥2 Shots
  // Beobachtungen tragen — sonst bleibt das Panel komplett still.
  const kannRechnen = (daten.n_kandidaten || 0) > 0 || (daten.n_obs_shots || 0) >= 2;
  if (!offen && !kannRechnen) return null;

  const rechnen = async () => {
    setLaufend(true);
    setMeldung("Netz-Selfcal rechnet — Ernte + geteilter Bündelausgleich …");
    try {
      const { job_id } = await netzSelfcalVorschlagStarten(seqId);
      await pollJob(job_id, {
        onProgress: ({ message }) => setMeldung(message || "läuft …"),
      });
      toast.success("Netz-Selfcal fertig — Vorschlag liegt vor");
      laden();
    } catch (e) {
      toast.error(fehlerText(e, "Netz-Selfcal fehlgeschlagen"));
    } finally {
      setLaufend(false);
      setMeldung("");
    }
  };

  const uebernehmen = async () => {
    setLaufend(true);
    try {
      const r = await netzSelfcalVorschlagUebernehmen(seqId);
      toast.success(`${r.n_uebernommen} Netz-Pose(n) übernommen`);
      laden();
      onApplied?.();
    } catch (e) {
      toast.error(fehlerText(e, "Übernahme fehlgeschlagen"));
    } finally {
      setLaufend(false);
    }
  };

  const ablehnen = async () => {
    setLaufend(true);
    try {
      await netzSelfcalVorschlagAblehnen(seqId);
      toast.success("Vorschlag abgelehnt — nichts übernommen");
      laden();
    } catch (e) {
      toast.error(fehlerText(e, "Ablehnen fehlgeschlagen"));
    } finally {
      setLaufend(false);
    }
  };

  // ── Zustand A: kein offener Vorschlag → kompakter Rechnen-Anstoß ──
  if (!offen) {
    // Dauer-Schätzung, damit klar ist, was der Knopf auslöst. Ernte:
    // ~3 s je Frame bei 0,5-s-Raster (exakt über ernte_videozeit_s vom
    // Backend, sonst grob 3,5 min je Kandidat). Bündelausgleich: der
    // dominante Posten — NACHKALIBRIERT am echten 10-Shot-Lauf vom
    // 02./03.09. (~3,5 h CPU gesamt, davon ~0,5 h Ernte): je Shot mit
    // Beobachtungen ~8–18 min, abhängig von der Shot-Länge (40-Frame-
    // Deckel). Die erste Anzeige (45–60 min) war zu optimistisch.
    const ernteMin = daten.ernte_videozeit_s != null
      ? Math.round(((daten.ernte_videozeit_s / 0.5) * 3) / 60)
      : Math.round((daten.n_kandidaten || 0) * 3.5);
    const nBa = (daten.n_obs_shots || 0) + (daten.n_kandidaten || 0);
    const minGesamt = ernteMin + nBa * 8;
    const maxGesamt = ernteMin + nBa * 18;
    const fmt = (a, b) => (b < 100
      ? `~${Math.max(5, Math.round(a / 5) * 5)}–${Math.round(b / 5) * 5} min`
      : `~${Math.round((a / 60) * 2) / 2}–${Math.round((b / 60) * 2) / 2} Std.`);
    const dauerText = fmt(minGesamt, maxGesamt);
    return (
      <div
        data-testid="netz-selfcal-anstoss"
        className="rounded border border-cyan-500/30 bg-cyan-500/5 px-2 py-1.5 space-y-1"
      >
        <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">
          🕸 Netz-Selfcal
        </div>
        <div className="text-[11px] text-cyan-100/80">
          {daten.n_kandidaten > 0
            ? `${daten.n_kandidaten} ankerlose Wide-Shots könnten ihre Kamera aus Netz-Beobachtungen bekommen.`
            : `${daten.n_obs_shots} Shots tragen Netz-Beobachtungen — Vorschlag neu rechnen.`}
          {daten.n_uebernommen > 0 ? ` (${daten.n_uebernommen} bereits übernommen)` : ""}
        </div>
        <div data-testid="netz-selfcal-dauer" className="text-[10px] text-cyan-100/60">
          Dauer: {dauerText}
          {daten.n_kandidaten > 0
            ? ` (Netz-Ernte von ${daten.n_kandidaten} Shot${daten.n_kandidaten === 1 ? "" : "s"} + Bündelausgleich)`
            : " (nur Bündelausgleich, Ernte liegt vor)"}
          {" — läuft im Hintergrund weiter, das Ergebnis erscheint hier."}
        </div>
        {daten.laufender_job?.id ? (
          <div data-testid="netz-selfcal-lauf"
               className="text-[10px] text-cyan-200/80 font-mono">
            ⏳ Lauf aktiv ({Math.round((daten.laufender_job.progress || 0) * 100)} %):{" "}
            {daten.laufender_job.message || "läuft …"}
          </div>
        ) : (
          <div className="flex gap-2 items-center">
            <button
              data-testid="netz-selfcal-rechnen"
              disabled={laufend}
              onClick={rechnen}
              className="rounded border border-cyan-500/50 bg-cyan-500/10 px-1.5 py-0.5 text-[10px] text-cyan-300 hover:bg-cyan-500/20 disabled:opacity-50"
              title={`Erntet fehlende Wide-Shots (gedeckelt, ~3 s je Frame) und rechnet den geteilten Bündelausgleich — zusammen ${dauerText}. Ergebnis kommt als Vorschlag — nichts wird still angewandt.`}
            >
              ▶ Netz-Selfcal rechnen ({dauerText})
            </button>
            {laufend && meldung && (
              <span className="text-[10px] text-cyan-200/70 font-mono">{meldung}</span>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── Zustand B: offener Vorschlag → Übernehmen/Ablehnen ────────────
  const shots = v.shots || {};
  const qualifiziert = Object.entries(shots)
    .filter(([, s]) => s.qualifiziert)
    .map(([sid, s]) => `Shot ${sid} (${s.rms_inlier_px} px, ${s.n_inliers} Stützstellen)`);
  const abgelehnt = Object.entries(shots).filter(([, s]) => !s.qualifiziert).length;

  return (
    <div
      data-testid="netz-selfcal-vorschlag"
      className="rounded border border-cyan-500/40 bg-cyan-500/5 px-2 py-1.5 space-y-1"
    >
      <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">
        🕸 Netz-Selfcal-Vorschlag · Kamera {Array.isArray(v.C_geteilt) ? v.C_geteilt.join(" / ") : "—"} m
      </div>
      <div className="text-[11px] text-cyan-100/90">
        {qualifiziert.length > 0
          ? `${qualifiziert.length} Shot(s) bestehen die Gates: ${qualifiziert.join(" · ")}`
          : "kein Shot besteht die Gates"}
        {abgelehnt > 0 ? ` — ${abgelehnt} weitere bleiben ehrlich draußen (Gates/abseits)` : ""}
      </div>
      <div className="text-[10px] text-cyan-100/60">
        Posen aus reinen Netz-Beobachtungen; greifen nur in Shots ohne
        manuelle Anker-Selfcal. Härtetest bleibt das Klick-Gate — px-Werte
        hier sind Netz-Fit-Erklärung, nicht Klick-Wahrheit.
      </div>
      <div className="flex gap-2">
        <button
          data-testid="netz-selfcal-uebernehmen"
          disabled={laufend || qualifiziert.length === 0}
          onClick={uebernehmen}
          className="rounded border border-emerald-500/50 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-50"
          title="Qualifizierte Posen nach netz_selfcal.{shot} übernehmen. Der Provider nutzt sie hinter dem Gate (NETZ_SELFCAL_PROVIDER_GATE_PX); Rückbau je Shot über DELETE netz-selfcal/{shot}."
        >
          ✓ Übernehmen
        </button>
        <button
          data-testid="netz-selfcal-ablehnen"
          disabled={laufend}
          onClick={ablehnen}
          className="rounded border border-slate-500/50 bg-slate-500/10 px-1.5 py-0.5 text-[10px] text-slate-300 hover:bg-slate-500/20 disabled:opacity-50"
          title="Nichts übernehmen. Ein neuer Job-Lauf darf später einen frischen Vorschlag legen."
        >
          ✕ Ablehnen
        </button>
        {laufend && meldung && (
          <span className="text-[10px] text-cyan-200/70 font-mono">{meldung}</span>
        )}
      </div>
    </div>
  );
}
